import { Document } from "mongoose"

export type ProductFeeType = 'flat' | 'percent' | 'flat_plus_percent'

export type ProductFee = {
  label: string
  type: ProductFeeType
  flatAmount?: number
  percent?: number
  finePrint?: string
}

export const FEE_PROJECT_TYPES = ['residential', 'commercial', 'non-profit'] as const

export type FeeProjectType = typeof FEE_PROJECT_TYPES[number]

/** Fees are configured once per tenant, keyed by project type. A missing key uses the legacy schedule. */
export type FeesByProductType = Partial<Record<FeeProjectType, ProductFee[]>>

/** DocuSign loan documents have this many fee description/amount pairs. */
export const MAX_FEE_SCHEDULE_LINES = 8

/**
 * Configured fees share the document lines with document stamps and a
 * possible loan modification fee, so the form reserves those slots.
 */
export function maxConfiguredProductFees(productType: string | undefined): number {
  if (productType === 'commercial' || productType === 'non-profit') {
    return 7
  }
  return 6
}

const PRODUCT_FEE_TYPES: ProductFeeType[] = ['flat', 'percent', 'flat_plus_percent']
const HTML_MARKUP = /[<>]/

function finiteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

function isProductFeeType(value: unknown): value is ProductFeeType {
  return PRODUCT_FEE_TYPES.includes(value as ProductFeeType)
}

export function validateProductFees(
  fees: ProductFee[] | undefined | null,
  productType: string | undefined
): string | null {
  if (fees === undefined || fees === null) return null
  if (!Array.isArray(fees)) return 'Fees must be a list'

  const maxFees = maxConfiguredProductFees(productType)
  if (fees.length > maxFees) {
    const typeLabel = productType === 'commercial' || productType === 'non-profit'
      ? productType
      : 'residential'
    return `A ${typeLabel} product can have at most ${maxFees} fees`
  }

  for (let index = 0; index < fees.length; index++) {
    const fee = fees[index]
    const position = index + 1
    if (!fee || typeof fee !== 'object') {
      return `Fee ${position} is invalid`
    }

    const label = typeof fee.label === 'string' ? fee.label.trim() : ''
    if (!label) return `Fee ${position} needs a label`
    if (label.length > 80) return `Fee ${position} label must be 80 characters or fewer`
    if (HTML_MARKUP.test(label)) return `Fee ${position} label cannot contain HTML`
    if (fee.finePrint !== undefined && typeof fee.finePrint !== 'string') {
      return `Fee ${position} fine print must be text`
    }
    const finePrint = fee.finePrint?.trim() || ''
    if (finePrint.length > 1000) return `Fee ${position} fine print must be 1000 characters or fewer`
    if (HTML_MARKUP.test(finePrint)) return `Fee ${position} fine print cannot contain HTML`
    if (!isProductFeeType(fee.type)) return `Fee ${position} has an invalid type`

    const needsFlat = fee.type === 'flat' || fee.type === 'flat_plus_percent'
    const needsPercent = fee.type === 'percent' || fee.type === 'flat_plus_percent'

    if (needsFlat) {
      const flatAmount = finiteNumber(fee.flatAmount)
      if (flatAmount === undefined) return `Fee ${position} needs a flat amount`
      if (flatAmount < 0) return `Fee ${position} flat amount must be zero or greater`
    }

    if (needsPercent) {
      const percent = finiteNumber(fee.percent)
      if (percent === undefined) return `Fee ${position} needs a percent`
      if (percent < 0 || percent > 100) return `Fee ${position} percent must be between 0 and 100`
    }
  }

  return null
}

export function validateFeesByProductType(
  feesByProductType: FeesByProductType | undefined | null
): string | null {
  if (feesByProductType === undefined || feesByProductType === null) return null
  if (typeof feesByProductType !== 'object' || Array.isArray(feesByProductType)) {
    return 'Fees must be configured by product type'
  }

  for (const projectType of FEE_PROJECT_TYPES) {
    if (feesByProductType[projectType] === undefined) continue
    const error = validateProductFees(feesByProductType[projectType], projectType)
    if (error) return `${projectType}: ${error}`
  }

  return null
}

export function sanitizeProductFees(fees: ProductFee[]): ProductFee[] {
  return fees.map(fee => {
    const sanitized: ProductFee = {
      label: fee.label.trim(),
      type: fee.type,
    }
    const finePrint = fee.finePrint?.trim()
    if (finePrint) sanitized.finePrint = finePrint
    if (fee.type === 'flat' || fee.type === 'flat_plus_percent') {
      sanitized.flatAmount = finiteNumber(fee.flatAmount)
    }
    if (fee.type === 'percent' || fee.type === 'flat_plus_percent') {
      sanitized.percent = finiteNumber(fee.percent)
    }
    return sanitized
  })
}

export function sanitizeFeesByProductType(feesByProductType: FeesByProductType): FeesByProductType {
  const sanitized: FeesByProductType = {}
  for (const projectType of FEE_PROJECT_TYPES) {
    const fees = feesByProductType[projectType]
    if (Array.isArray(fees)) {
      sanitized[projectType] = sanitizeProductFees(fees)
    }
  }
  return sanitized
}

export interface IProduct {
  _id?: string
  id?: string
  productType: string
  term: number
  interestOnlyPeriod: number
  apr: number
  autoPayAPR: number
  interestRateWithAutopay: number
  interestRateWithoutAutopay: number
  paymentFactorWithAutopay: number
  paymentFactorWithoutAutopay: number
  dealerFee: number
  maxPpw: number
  maxLoanAmount: number
  minLoanAmount: number
  disclosures: string
  approvedStates: string[]
  productDescription: string
  detailedProductDescriptionClientHtml: string
  detailedProductDescriptionInstallerHtml: string
  decisionProProductName: string
  equipmentTypes: string[]
  effectiveDate?: Date
  expirationDate?: Date
  prequalExpirationDate?: Date
  isValidForIncentive?: boolean
}

export type ProductDocument = IProduct & Document

export const ALLOWED_PRODUCT_UPDATE_FIELDS = [
  'productType',
  'term',
  'interestOnlyPeriod',
  'apr',
  'autoPayAPR',
  'interestRateWithAutopay',
  'interestRateWithoutAutopay',
  'paymentFactorWithAutopay',
  'paymentFactorWithoutAutopay',
  'dealerFee',
  'maxPpw',
  'maxLoanAmount',
  'minLoanAmount',
  'disclosures',
  'approvedStates',
  'productDescription',
  'detailedProductDescriptionClientHtml',
  'detailedProductDescriptionInstallerHtml',
  'decisionProProductName',
  'equipmentTypes',
  'effectiveDate',
  'expirationDate',
  'prequalExpirationDate',
  'isValidForIncentive',
] as const

export const getProductName = (product: IProduct, incentiveSelected: boolean): string => {
  if (product?.term === 366 && incentiveSelected === true) {
    return 'MCGB Pilot2 30 Year 2.99%'
  } else if (product?.term === 246 && incentiveSelected === true) {
    return 'MCGB Pilot2 20 Year 2.99%'
  } else {
    return product?.decisionProProductName
  }
}

export const EQUIPMENT_TYPES_LABELS: Record<string, string> = {
  'tesla-roof': 'Tesla Roof + Powerwall 3',
  'tesla-solar': 'Tesla Solar Panels + Powerwall 3',
  'standard-solar': 'Standard Solar',
  'storage-only': 'Storage Only'
}
